import { isPlainObject } from "es-toolkit";
import type { CadPointCloudAnalysis } from "@/store/point-cloud-analysis-tool-slice";

export type CadDeviationSettings = Pick<
  CadPointCloudAnalysis,
  "tolerance" | "searchDistance" | "colormap" | "deviationColouring"
>;

export type CadDeviationColouring = { unmatchedOpacity?: number } & (
  | {
      mode: "continuous";
      unmatched: "source" | "grey";
      outerBandLimit?: number;
    }
  | { mode: "bands"; unmatched: "source" | "grey"; outerBandLimit: number }
);

export const CAD_BAND_COLOURS: [string, string, string, string, string] = [
  "#0000FF",
  "#00FFFF",
  "#00FF00",
  "#FFFF00",
  "#FF0000",
];
export const CAD_UNMATCHED_COLOUR = "#999999";
export const DEFAULT_CAD_COLOURING: CadDeviationColouring = {
  mode: "bands",
  unmatched: "grey",
  outerBandLimit: 0.025,
};

/**
 * @param value Untrusted display configuration.
 * @param tolerance Accepted signed distance magnitude, in metres.
 * @param searchDistance Maximum distance for a usable match.
 * @returns Whether the configuration and thresholds are consistent.
 */
export function isCadDeviationColouring(
  value: unknown,
  tolerance: number,
  searchDistance: number,
): value is CadDeviationColouring {
  if (
    !Number.isFinite(tolerance) ||
    tolerance < 0 ||
    !Number.isFinite(searchDistance) ||
    searchDistance < tolerance ||
    !isPlainObject(value)
  ) {
    return false;
  }
  if (value.unmatched !== "source" && value.unmatched !== "grey") return false;
  if (
    value.unmatchedOpacity !== undefined &&
    (typeof value.unmatchedOpacity !== "number" ||
      !Number.isFinite(value.unmatchedOpacity) ||
      value.unmatchedOpacity < 0 ||
      value.unmatchedOpacity > 1)
  ) {
    return false;
  }
  const outer = value.outerBandLimit;
  if (
    outer !== undefined &&
    (typeof outer !== "number" || !Number.isFinite(outer) || outer < 0)
  ) {
    return false;
  }
  if (value.mode === "continuous") return true;
  return (
    value.mode === "bands" &&
    typeof outer === "number" &&
    tolerance < outer &&
    outer < searchDistance
  );
}

/**
 * @param tolerance Existing tolerance, never modified by this helper.
 * @param searchDistance Existing search range, never widened implicitly.
 * @returns A valid initial outer limit, or undefined if bands cannot fit.
 */
export function initialCadOuterLimit(
  tolerance: number,
  searchDistance: number,
): number | undefined {
  if (
    !Number.isFinite(tolerance) ||
    tolerance < 0 ||
    !Number.isFinite(searchDistance) ||
    searchDistance <= tolerance
  ) {
    return;
  }
  const preferredMultiplier = 2.5;
  const preferred = tolerance * preferredMultiplier;
  return preferred > tolerance && preferred < searchDistance
    ? preferred
    : tolerance + (searchDistance - tolerance) / 2;
}

/**
 * @param analysis CAD display thresholds and palette.
 * @returns Colour stops positioned across the complete signed search interval.
 */
export function cadScaleColours(
  analysis: CadDeviationSettings,
): Array<{ color: string; ratio: number }> {
  const { tolerance, searchDistance, colormap, deviationColouring } = analysis;
  const first = colormap[0]?.color ?? CAD_UNMATCHED_COLOUR;
  if (!(searchDistance > 0)) {
    return [
      { color: first, ratio: 0 },
      { color: first, ratio: 1 },
    ];
  }
  function ratio(distance: number): number {
    return Math.max(0, Math.min(1, 0.5 + distance / (2 * searchDistance)));
  }
  if (deviationColouring?.mode === "bands") {
    const boundaries = [
      -searchDistance,
      -deviationColouring.outerBandLimit,
      -tolerance,
      tolerance,
      deviationColouring.outerBandLimit,
      searchDistance,
    ];
    return CAD_BAND_COLOURS.flatMap((color, index) => [
      { color, ratio: ratio(boundaries[index]) },
      { color, ratio: ratio(boundaries[index + 1]) },
    ]);
  }
  return [
    { color: first, ratio: 0 },
    ...colormap.map(({ color, value }) => ({
      color,
      ratio: ratio((value * 2 - 1) * tolerance),
    })),
    { color: colormap[colormap.length - 1]?.color ?? first, ratio: 1 },
  ];
}
